function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx } from "react/jsx-runtime";
import { createColumnHelper } from '@tanstack/react-table';
import { getLatestAppVersion } from '@repo/utils';
import { ModuleDependencyRenderer } from './renderers/ModuleDependencyRenderer';
import { ModuleOrApplicationLinkRenderer } from './renderers/ModuleOrApplicationLinkRenderer';
import { TagsRenderer } from './renderers/TagsRenderer';
const columnHelper = createColumnHelper();
export const configureColumnDefs = (applications, organization, project)=>{
    if (applications.length === 0 || !organization || !project) {
        return [];
    }
    return [
        columnHelper.accessor((row)=>row, {
            id: 'moduleName',
            header: ()=>'Modules',
            size: 200,
            cell: (inf)=>{
                const row = inf.getValue();
                return /*#__PURE__*/ _jsx(ModuleOrApplicationLinkRenderer, {
                    organization: organization,
                    project: project,
                    application: row.application,
                    value: row.moduleName
                });
            }
        }),
        ...applications.map((application)=>columnHelper.accessor((row)=>row, {
                id: application.id,
                header: ()=>/*#__PURE__*/ _jsx(ModuleOrApplicationLinkRenderer, {
                        organization: organization,
                        type: "application",
                        project: project,
                        application: application.name,
                        value: application.name
                    }),
                size: 150,
                minSize: 120,
                maxSize: 300,
                cell: (inf)=>{
                    const row = inf.getValue();
                    return /*#__PURE__*/ _jsx(ModuleDependencyRenderer, {
                        value: row[application.id]
                    });
                }
            })),
        columnHelper.accessor((row)=>row, {
            id: 'moduleRequires',
            header: ()=>'Requires',
            size: 150,
            cell: (inf)=>{
                const row = inf.getValue();
                return /*#__PURE__*/ _jsx(TagsRenderer, {
                    value: row.moduleRequires
                });
            }
        })
    ];
};
export const convertToRowData = (applications, organization)=>{
    if (applications.length === 0 || !organization) {
        return [];
    }
    const modules = applications.map((param)=>{
        let { id, name, versions } = param;
        const latestVersion = getLatestAppVersion(versions);
        if (!latestVersion) {
            return [];
        }
        return latestVersion.modules.map((mod)=>_object_spread_props(_object_spread({}, mod), {
                organization,
                absoluteId: "".concat(id, ":").concat(mod.name),
                applicationId: id,
                application: name
            }));
    }).flat();
    const modulesById = modules.reduce((memo, param)=>{
        let { name, applicationId } = param;
        memo["".concat(applicationId, ":").concat(name)] = {
            name,
            applicationId,
            applications: {}
        };
        return memo;
    }, {});
    applications.forEach((param)=>{
        let { id: consumingApplication, versions } = param;
        const latestVersion = getLatestAppVersion(versions);
        if (!latestVersion) {
            return;
        }
        latestVersion.consumes.forEach((param)=>{
            let { name, applicationID, usedIn } = param;
            var _applications_find;
            const applicationId = (_applications_find = applications.find((application)=>application.name === applicationID)) === null || _applications_find === void 0 ? void 0 : _applications_find.id;
            if (!applicationId) {
                return;
            }
            const modules = modulesById["".concat(applicationId, ":").concat(name)];
            if (modules) {
                modules.applications[consumingApplication] = {
                    count: usedIn === null || usedIn === void 0 ? void 0 : usedIn.length,
                    type: ''
                };
            }
        });
    });
    const findVersion = (applicationId, name)=>{
        var _applications_find;
        const { versions } = (_applications_find = applications.find((param)=>{
            let { id } = param;
            return id === applicationId;
        })) !== null && _applications_find !== void 0 ? _applications_find : {};
        if (!versions) {
            return '';
        }
        const latestVersion = getLatestAppVersion(versions);
        if (!latestVersion) {
            return '';
        }
        const ov = latestVersion.overrides.find((param)=>{
            let { name: ovName } = param;
            return ovName === name;
        });
        return ov ? " (".concat(ov.version, ")") : '';
    };
    const getModuleApplicationsData = (applications, absoluteId)=>{
        return applications.reduce((memo, param)=>{
            let { id } = param;
            const moduleApplication = modulesById[absoluteId];
            if ((moduleApplication === null || moduleApplication === void 0 ? void 0 : moduleApplication.applicationId) === id) {
                memo[id] = {
                    type: 'up'
                };
            }
            const application = moduleApplication === null || moduleApplication === void 0 ? void 0 : moduleApplication.applications[id];
            if (application) {
                memo[id] = {
                    type: 'down',
                    count: application.count
                };
            }
            return memo;
        }, {});
    };
    return modules.map((module)=>{
        return _object_spread({
            moduleName: module.name,
            application: module.application,
            moduleRequires: module.requires.map((name)=>"".concat(name).concat(findVersion(module.applicationId, name)))
        }, getModuleApplicationsData(applications, module.absoluteId));
    });
};
